import * as fcl from "@onflow/fcl"
import {getSdkError, init} from "@onflow/fcl-wc"
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"

import {DEFAULT_APP_METADATA, DEFAULT_PROJECT_ID} from "../constants"
import {WCService} from "../flow/config"
import {getRequiredNamespaces} from "../helpers"
import {modalType} from "../components/Modal"
import {isAccountInitialized as isAccountInitializedTx} from "../flow/script.is-account-initialized"
import {getFRGBalance as frgBalanceScript} from "../flow/script.get-frg-balance"
import {getFlowBalance as flowBalanceScript} from "../flow/script.get-flow-balance"

export const ClientContext = createContext({})

export function ClientContextProvider({children}) {
    const [client, setClient] = useState(null)
    const [pairings, setPairings] = useState()
    const [session, setSession] = useState()

    const [isFetchingBalances, setIsFetchingBalances] = useState(false)
    const [isInitializing, setIsInitializing] = useState(false)

    const [balances, setBalances] = useState({})
    const [accounts, setAccounts] = useState([])
    const [chains, setChains] = useState([])

    const [currentUser, setCurrentUser] = useState(null)
    const [isAccountInitStateLoading, setIsAccountInitStateLoading] =
        useState(false)
    const [isAccountInitialized, setIsAccountInitialized] = useState(false)
    const [frgBalance, setFrgBalance] = useState(0.0)
    const [flowBalance, setFlowBalance] = useState(0.0)
    const [showModal, setShowModal] = useState(false)
    const [modalData, setModalData] = useState({})
    const [unityUnloading, setUnityUnloading] = useState(false)

    const reset = () => {
        setSession(null)
        setBalances({})
        setAccounts([])
        setChains([])
    }

    const getAccountBalances = async _accounts => {
        console.log("getAccountBalances", _accounts)

        setIsFetchingBalances(true)
        try {
            const arr = await Promise.all(
                _accounts.map(async account => {
                    const [namespace, reference, address] = account.split(":")
                    // const chainId = `${namespace}:${reference}`
                    // const assets = await apiGetAccountBalance(address, chainId)
                    const balance = "0.00"
                    console.log("Get Account Balance", address, namespace, reference)

                    return {account, balance}
                })
            )

            const balances = {}
            arr.forEach(({account, balance}) => {
                balances[account] = balance
            })
            setBalances(balances)
        } catch (e) {
            console.error(e)
        } finally {
            setIsFetchingBalances(false)
        }
    }

    const onSessionConnected = useCallback(async _session => {
        const allNamespaceAccounts = Object.values(_session.namespaces)
            .map(namespace => namespace.accounts)
            .flat()
        const allNamespaceChains = Object.keys(_session.namespaces)
        setShowModal(false)
        setSession(_session)
        setChains(allNamespaceChains)
        setAccounts(allNamespaceAccounts)
        await getAccountBalances(allNamespaceAccounts)
    }, [])

    const connect = useCallback(
        async opts => {
            if (typeof client === "undefined") {
                throw new Error("WalletConnect is not initialized")
            }
            try {
                // use this to update config?
                const requiredNamespaces = getRequiredNamespaces(chains)
                console.log(
                    "requiredNamespaces config for connect:",
                    chains,
                    requiredNamespaces["flow"]
                )
                try {
                    const res = await fcl.reauthenticate(opts) // using Discovery to show injected wallets from [wallets]
                    console.log("res", res)
                } catch (error) {
                    console.error(error, "Error on Authn")
                }
                if (client.session.length) {
                    const lastKeyIndex = client.session.keys.length - 1
                    const _session = client.session.get(client.session.keys[lastKeyIndex])
                    console.log("session", _session, client.session.keys[lastKeyIndex])
                    await onSessionConnected(_session)
                    // Update known pairings after session is connected.
                    setPairings(client.pairing.getAll({active: true}))
                }
            } catch (e) {
                console.error(e)
                // ignore rejection
            }
        },
        [chains, client, onSessionConnected]
    )

    const disconnect = useCallback(async () => {
        if (typeof client === "undefined") {
            throw new Error("WalletConnect is not initialized")
        }
        if (typeof session === "undefined") {
            throw new Error("Session is not connected")
        }
        await client.disconnect({
            topic: session.topic,
            reason: getSdkError("USER_DISCONNECTED"),
        })
        // Reset app state after disconnect.
        reset()
        fcl.unauthenticate()
    }, [client, session])

    const _subscribeToEvents = useCallback(
        async _client => {
            if (typeof _client === "undefined") {
                throw new Error("WalletConnect is not initialized")
            }

            _client.on("session_ping", args => {
                console.log("EVENT", "session_ping", args)
            })

            _client.on("session_event", args => {
                console.log("EVENT", "session_event", args)
            })

            _client.on("session_proposal", args => {
                console.log("EVENT", "session_proposal", args)
            })

            _client.on("session_extend", args => {
                console.log("EVENT", "session_extend", args)
            })

            _client.on("pairing_ping", args => {
                console.log("EVENT", "pairing_ping", args)
            })

            _client.on("session_expire", args => {
                console.log("EVENT", "session_expire", args)
            })

            _client.on("pairing_delete", args => {
                console.log("EVENT", "pairing_delete", args)
            })

            _client.on("pairing_expire", args => {
                console.log("EVENT", "pairing_expire", args)
            })

            _client.on("session_request", args => {
                console.log("EVENT", "session_request", args)
            })

            _client.on("proposal_expire", args => {
                console.log("EVENT", "proposal_expire", args)
            })

            _client.on("session_update", ({topic, params}) => {
                console.log("EVENT", "session_update", {topic, params})
                const {namespaces} = params
                const _session = _client.session.get(topic)
                const updatedSession = {..._session, namespaces}
                onSessionConnected(updatedSession)
            })

            _client.on("session_delete", () => {
                console.log("EVENT", "session_delete")
                reset()
                fcl.unauthenticate()
            })
        },
        [onSessionConnected]
    )

    const _checkPersistedState = useCallback(
        async _client => {
            if (typeof _client === "undefined") {
                throw new Error("WalletConnect is not initialized")
            }
            // populates existing pairings to state
            setPairings(_client.pairing.getAll({active: true}))

            if (typeof session !== "undefined") return
            // populates (the last) existing session to state
            if (_client.session.length) {
                const lastKeyIndex = _client.session.keys.length - 1
                const _session = _client.session.get(_client.session.keys[lastKeyIndex])

                await onSessionConnected(_session)
                return _session
            }
        },
        [session, onSessionConnected]
    )

    const createClient = useCallback(async () => {
        try {
            setIsInitializing(true)

            const {FclWcServicePlugin, client} = await init({
                projectId: DEFAULT_PROJECT_ID,
                metadata: DEFAULT_APP_METADATA,
                includeBaseWC: false, // show base walletconnect in Discovery UI/API
                wallets: [WCService],
                wcRequestHook: async (data) => {
                    console.log('WRH WC Request data', data)
                    if (data.type ==='signing_request' && data.method === 'flow_authn') {
                        setModalData({
                            type: modalType.InfoSpinner,
                            title: `Establishing Session`,
                            message: "Please open the Dapper SC app to proceed"
                        })
                        setShowModal(true)
                    } else if (data.type === 'signing_request') {
                        setModalData({
                            type: modalType.InfoSpinner,
                            title: `Signing Request`,
                            message: "Please open the Dapper SC app to approve/reject this transaction"
                        })
                        setShowModal(true)
                    }
                }
            })
            fcl.pluginRegistry.add(FclWcServicePlugin)



            const _client = client
            console.log("CLIENT INIT: ", _client)
            setClient(_client)
            await _subscribeToEvents(_client)
            await _checkPersistedState(_client)
        } catch (err) {
            throw err
        } finally {
            setIsInitializing(false)
        }
    }, [_checkPersistedState, _subscribeToEvents])

    useEffect(() => {
        if (!client) {
            createClient()
        }
    }, [client, createClient])




    const checkIsAccountInitialized = useCallback(() => {
        if (currentUser?.addr) {
            setIsAccountInitStateLoading(true)
            isAccountInitializedTx(currentUser?.addr).then(data => {
                setIsAccountInitialized(data)
                setIsAccountInitStateLoading(false)
            })
        }
    }, [currentUser?.addr])

    const updateFRGBalance = useCallback(() => {
        if(currentUser?.addr) {
            frgBalanceScript(currentUser?.addr).then(data => {
                setFrgBalance(data)
            })
            flowBalanceScript(currentUser?.addr).then(data => {
                setFlowBalance(data)
            })
        }
    }, [currentUser?.addr])

    useEffect(
        () =>
            fcl.currentUser().subscribe(newUser => {
                if (newUser?.loggedIn) {
                    setCurrentUser(newUser)
                } else {
                    setCurrentUser(null)
                }
            }),
        []
    )

    useEffect(() => {
        checkIsAccountInitialized()
        updateFRGBalance()
    }, [checkIsAccountInitialized, updateFRGBalance])


    const value = useMemo(
        () => ({
            pairings,
            isInitializing,
            balances,
            isFetchingBalances,
            accounts,
            chains,
            client,
            session,
            connect,
            disconnect,
            setChains,
            currentUser,
            isAccountInitStateLoading,
            isAccountInitialized,
            checkIsAccountInitialized,
            frgBalance,
            flowBalance,
            updateFRGBalance,
            showModal,
            setShowModal,
            setModalData,
            modalData,
            unityUnloading,
            setUnityUnloading
        }),
        [
            pairings,
            isInitializing,
            balances,
            isFetchingBalances,
            accounts,
            chains,
            client,
            session,
            connect,
            disconnect,
            setChains,
            currentUser,
            isAccountInitStateLoading,
            isAccountInitialized,
            checkIsAccountInitialized,
            frgBalance,
            flowBalance,
            updateFRGBalance,
            showModal,
            setShowModal,
            setModalData,
            modalData,
            unityUnloading,
            setUnityUnloading
        ]
    )

    return (
        <ClientContext.Provider
            value={{
                ...value,
            }}
        >
            {children}
        </ClientContext.Provider>
    )
}

export function useClientContext() {
    const context = useContext(ClientContext)
    if (context === undefined) {
        throw new Error(
            "useClientContext must be used within a ClientContextProvider"
        )
    }
    return context
}
